import React, { useState, useEffect } from "react";
import Header from "../../Header/Header";
import Sidebar from "../../SideBar/SideBar";
import Footer from "../../Footer/Footer";
import { Link, useLocation, useHistory } from "react-router-dom";
import { registerapi } from "../../HttpRequest/beforelogin";
import axiosInstance from "../../HttpRequest/instance";

const instance = axiosInstance();

const SuperAdminDashboard = () => {
  const location = useLocation();
  const history = useHistory();

  // Active Tab State (overview, admin, user, application-assistant, counsellor, loan-officer, importData)
  const [activeTab, setActiveTab] = useState("overview");

  // Read URL query parameter for tab if present (?tab=admin etc)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
    } else {
      setActiveTab("overview");
    }
  }, [location.search]);

  // Overall KPI Metrics
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    totalStudents: 0,
    totalAssistants: 0,
    totalCounsellors: 0,
    totalLoanOfficers: 0,
  });

  // Table Data & Pagination State
  const [usersList, setUsersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Modal Dialog States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // User Form State (for Create & Edit)
  const [formData, setFormData] = useState({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    whatsappNumber: "",
    dateOfBirth: "",
    role: "admin",
    yourCollageName: "N/A",
    city: "N/A",
    orgName: "",
  });

  const [formMsg, setFormMsg] = useState({ type: "", text: "" });
  const [actionLoading, setActionLoading] = useState(false);

  // Master Data Import State
  const [excelFile, setExcelFile] = useState(null);
  const [importStatus, setImportStatus] = useState("");
  const [importing, setImporting] = useState(false);

  // Roles Metadata Configuration
  const roleConfigs = {
    admin: {
      title: "Admins",
      singular: "Admin",
      badgeClass: "badge-primary",
      softBadgeClass: "bg-primary text-white",
      icon: "fa-user-tie",
      desc: "Organization Administrators & Super Users",
      color: "#3d5ee1",
    },
    user: {
      title: "Students",
      singular: "Student",
      badgeClass: "badge-success",
      softBadgeClass: "bg-success text-white",
      icon: "fa-user-graduate",
      desc: "Registered Student Clients & Applicants",
      color: "#28a745",
    },
    "application-assistant": {
      title: "Application Assistants",
      singular: "Assistant",
      badgeClass: "badge-info",
      softBadgeClass: "bg-info text-white",
      icon: "fa-headset",
      desc: "Application Processing Operations Staff",
      color: "#17a2b8",
    },
    counsellor: {
      title: "Counsellors",
      singular: "Counsellor",
      badgeClass: "badge-warning",
      softBadgeClass: "bg-warning text-dark",
      icon: "fa-chalkboard-teacher",
      desc: "Student Counseling & Admission Staff",
      color: "#ffc107",
    },
    "loan-officer": {
      title: "Loan Officers",
      singular: "Loan Officer",
      badgeClass: "badge-secondary",
      softBadgeClass: "bg-secondary text-white",
      icon: "fa-hand-holding-usd",
      desc: "Education Loan & Financial Operations",
      color: "#6c757d",
    },
  };

  // Help Desk State
  const [helpDeskList, setHelpDeskList] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketQuery, setTicketQuery] = useState("");
  const [ticketMsg, setTicketMsg] = useState({ type: "", text: "" });

  // Fetch metrics & users on tab/page change
  useEffect(() => {
    fetchMetrics();
    if (activeTab === "helpDesk") {
      fetchHelpDeskTickets(currentPage, pageSize);
    } else if (activeTab !== "overview" && activeTab !== "importData") {
      fetchUsersForRole(activeTab, currentPage, pageSize);
    }
  }, [activeTab, currentPage, pageSize]);

  const fetchMetrics = async () => {
    try {
      const res = await instance.get("/all-users?role=user&page=0&size=1");
      if (res.data && res.data.metaData) {
        setMetrics((prev) => ({
          ...prev,
          totalStudents: res.data.metaData.totalElements || 0,
        }));
      }
    } catch (e) {
      console.log("Metrics fetch fallback", e);
    }
  };

  const fetchUsersForRole = async (role, page, size) => {
    setLoading(true);
    try {
      const res = await instance.get(`/all-users?role=${role}&page=${page}&size=${size}`);
      console.log("Fetched users for role", role, res);
      
      let rawData = res.data;
      if (rawData && rawData.data) {
        rawData = rawData.data;
      }
      
      let contentList = [];
      let metaObj = null;

      if (Array.isArray(rawData)) {
        contentList = rawData;
      } else if (rawData && Array.isArray(rawData.content)) {
        contentList = rawData.content;
        metaObj = rawData.metaData || rawData.meta;
      } else if (res.data && Array.isArray(res.data.content)) {
        contentList = res.data.content;
        metaObj = res.data.metaData || res.data.meta;
      }

      setUsersList(contentList);
      if (metaObj) {
        setTotalPages(metaObj.totalPages !== undefined ? metaObj.totalPages : 1);
        setTotalElements(metaObj.totalElements !== undefined ? metaObj.totalElements : contentList.length);
      } else {
        setTotalPages(1);
        setTotalElements(contentList.length);
      }
    } catch (err) {
      console.error("Error fetching users for role:", role, err);
      setUsersList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHelpDeskTickets = async (page, size) => {
    setLoading(true);
    try {
      const res = await instance.get(`/help-desk?page=${page}&size=${size}`);
      console.log("Fetched help desk tickets", res);
      let rawData = res.data;
      if (rawData && rawData.data) {
        rawData = rawData.data;
      }
      let contentList = [];
      let metaObj = null;

      if (Array.isArray(rawData)) {
        contentList = rawData;
      } else if (rawData && Array.isArray(rawData.content)) {
        contentList = rawData.content;
        metaObj = rawData.metaData || rawData.meta;
      } else if (res.data && Array.isArray(res.data.content)) {
        contentList = res.data.content;
        metaObj = res.data.metaData || res.data.meta;
      }

      setHelpDeskList(contentList);
      if (metaObj) {
        setTotalPages(metaObj.totalPages !== undefined ? metaObj.totalPages : 1);
        setTotalElements(metaObj.totalElements !== undefined ? metaObj.totalElements : contentList.length);
      } else {
        setTotalPages(1);
        setTotalElements(contentList.length);
      }
    } catch (err) {
      console.error("Error fetching help desk tickets:", err);
      setHelpDeskList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicketSubmit = async (e) => {
    e.preventDefault();
    if (!ticketQuery.trim()) return;

    setActionLoading(true);
    setTicketMsg({ type: "", text: "" });
    try {
      const loggedInUserId = sessionStorage.getItem("id") || localStorage.getItem("id") || sessionStorage.getItem("userId") || localStorage.getItem("userId") || 1;
      const payload = {
        query: ticketQuery,
        userId: Number(loggedInUserId),
        status: "Pending",
        createdDate: new Date().toISOString().split("T")[0],
      };
      const res = await instance.post("/help-desk", payload);
      if (res.status === 200 || res.status === 201) {
        setTicketMsg({ type: "success", text: "Help Desk request submitted successfully!" });
        setTicketQuery("");
        setTimeout(() => {
          setShowCreateTicketModal(false);
          setTicketMsg({ type: "", text: "" });
          fetchHelpDeskTickets(currentPage, pageSize);
        }, 1200);
      } else {
        setTicketMsg({ type: "error", text: "Failed to submit request." });
      }
    } catch (err) {
      console.error("Error submitting ticket:", err);
      setTicketMsg({ type: "error", text: "Error submitting request. Please try again." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await instance.put(`/help-desk/${ticketId}`, { status: newStatus });
      fetchHelpDeskTickets(currentPage, pageSize);
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error("Error updating ticket status:", err);
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(0);
    setSearchQuery("");
    history.push(`/superadmin?tab=${tabKey}`);
  };

  // Open Create User Modal
  const openCreateModalForRole = (roleKey) => {
    const targetRole = roleKey || (activeTab !== "overview" && activeTab !== "importData" ? activeTab : "admin");
    const loggedInOrgName = sessionStorage.getItem("orgName") || localStorage.getItem("orgName") || sessionStorage.getItem("organizationName") || localStorage.getItem("organizationName") || "";
    setFormData({
      id: null,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      whatsappNumber: "",
      dateOfBirth: "",
      role: targetRole,
      yourCollageName: "N/A",
      city: "N/A",
      orgName: loggedInOrgName,
    });
    setFormMsg({ type: "", text: "" });
    setShowCreateModal(true);
  };

  // Submit Create User
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setFormMsg({ type: "", text: "" });

    if (formData.password !== formData.confirmPassword) {
      setFormMsg({ type: "error", text: "Passwords do not match!" });
      return;
    }

    setActionLoading(true);
    try {
      const res = await registerapi(formData);
      if (res.status === 200 || res.status === 201) {
        setFormMsg({ type: "success", text: `User created successfully with role '${formData.role}'!` });
        setTimeout(() => {
          setShowCreateModal(false);
          fetchUsersForRole(activeTab, currentPage, pageSize);
        }, 1200);
      } else {
        setFormMsg({ type: "error", text: "Failed to create user. Please check form details." });
      }
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : err.message;
      setFormMsg({ type: "error", text: msg || "Error creating user." });
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit User Modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      id: user.id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      role: activeTab,
    });
    setFormMsg({ type: "", text: "" });
    setShowEditModal(true);
  };

  // Submit Edit User
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormMsg({ type: "", text: "" });
    setActionLoading(true);

    try {
      const res = await instance.put(`/all-users/${formData.id}`, formData);
      if (res.status === 200) {
        setFormMsg({ type: "success", text: "User updated successfully!" });
        setTimeout(() => {
          setShowEditModal(false);
          fetchUsersForRole(activeTab, currentPage, pageSize);
        }, 1200);
      } else {
        setFormMsg({ type: "error", text: "Failed to update user." });
      }
    } catch (err) {
      setFormMsg({ type: "error", text: "Error updating user." });
    } finally {
      setActionLoading(false);
    }
  };

  // Open Delete Confirmation Modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Confirm Delete User
  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await instance.delete(`/all-users/${selectedUser.id}`);
      setShowDeleteModal(false);
      fetchUsersForRole(activeTab, currentPage, pageSize);
    } catch (err) {
      console.error("Error deleting user:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Open View Details Modal
  const openViewModal = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  // Handle Master Data File Import
  const handleMasterDataImport = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      setImportStatus("Please select an Excel file to import.");
      return;
    }

    setImporting(true);
    setImportStatus("");
    const formDataObj = new FormData();
    formDataObj.append("file", excelFile);

    try {
      await instance.post("/master-data/upload", formDataObj, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportStatus("Master Data imported successfully!");
      setExcelFile(null);
    } catch (err) {
      setImportStatus("Failed to import Master Data. Please verify file format.");
    } finally {
      setImporting(false);
    }
  };

  // Filtered users by local search query
  const filteredUsers = usersList.filter((u) => {
    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
    const email = (u.email || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  // Filtered Help Desk tickets
  const filteredHelpDesk = helpDeskList.filter((t) => {
    const text = `${t.query || ""} ${t.userName || ""} ${t.userEmail || ""}`.toLowerCase();
    const query = searchQuery.toLowerCase();
    return text.includes(query);
  });

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* Standard Theme Page Header */}
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title mb-0">
                  {activeTab === "overview" && "Platform Overview"}
                  {roleConfigs[activeTab] && roleConfigs[activeTab].title}
                  {activeTab === "importData" && "Master Data Import"}
                  {activeTab === "helpDesk" && "Help Desk & Support Requests"}
                </h3>
              </div>
              {roleConfigs[activeTab] && (
                <div className="col-auto">
                  <button
                    className="btn btn-primary font-weight-bold px-3 py-2 shadow-sm"
                    onClick={() => openCreateModalForRole(activeTab)}
                  >
                    <i className="fas fa-plus me-1"></i> Add
                  </button>
                </div>
              )}
              {activeTab === "helpDesk" && (
                <div className="col-auto">
                  <button
                    className="btn btn-danger font-weight-bold px-3 py-2 shadow-sm"
                    onClick={() => setShowCreateTicketModal(true)}
                  >
                    <i className="fas fa-plus me-1"></i> Submit Request
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* TAB 1: OVERVIEW GRID */}
          {activeTab === "overview" && (
            <div>
              <div className="row">
                {Object.keys(roleConfigs).map((rKey) => (
                  <div className="col-xl-4 col-md-6 col-12 mb-4" key={rKey}>
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body p-4 d-flex flex-column justify-content-between">
                        <div>
                          <div className="d-flex align-items-center mb-3">
                            <div className="avatar avatar-md rounded-circle bg-light d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                                 style={{ width: "48px", height: "48px", color: roleConfigs[rKey].color, minWidth: "48px" }}>
                              <i className={`fas ${roleConfigs[rKey].icon} fa-lg`}></i>
                            </div>
                            <div>
                              <h5 className="font-weight-bold mb-0">{roleConfigs[rKey].title}</h5>
                              <small className="text-muted">{roleConfigs[rKey].desc}</small>
                            </div>
                          </div>
                        </div>

                        <div className="pt-3 border-top mt-2">
                          <button
                            className="btn btn-outline-primary btn-sm font-weight-bold btn-block rounded-pill py-2"
                            onClick={() => handleTabChange(rKey)}
                          >
                            Manage {roleConfigs[rKey].title} <i className="fas fa-arrow-right ms-1"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROLE SPECIFIC TABLES (Admin, User, Application Assistant, Counsellor, Loan Officer) */}
          {roleConfigs[activeTab] && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="font-weight-bold mb-0">
                  <i className={`fas ${roleConfigs[activeTab].icon} text-primary me-2`}></i> {roleConfigs[activeTab].title}
                </h5>
                {/* Search Input */}
                <div className="input-group input-group-sm mt-2 mt-sm-0" style={{ width: "260px" }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                    <p className="mt-2 text-muted font-weight-bold">Loading records...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="px-4 py-3">User Profile</th>
                          <th className="py-3">Email</th>
                          <th className="py-3">Phone</th>
                          <th className="py-3">Created Date</th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers && filteredUsers.length > 0 ? (
                          filteredUsers.map((u) => (
                            <tr key={u.id}>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="avatar avatar-sm bg-primary text-white rounded-circle me-3 flex-shrink-0 d-flex align-items-center justify-content-center font-weight-bold" style={{ width: "36px", height: "36px", fontSize: "14px" }}>
                                    {(u.firstName || "U").charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <h6 className="font-weight-bold mb-0">
                                      {u.firstName} {u.lastName}
                                    </h6>
                                    <small className="text-muted">ID: #{u.id}</small>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3">{u.email}</td>
                              <td className="py-3">{u.phoneNumber || "N/A"}</td>
                              <td className="py-3">{u.createdDate || "N/A"}</td>
                              <td className="py-3">
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    className="btn btn-outline-info btn-sm rounded-circle d-flex justify-content-center align-items-center p-0"
                                    style={{ width: "32px", height: "32px" }}
                                    title="View Profile"
                                    onClick={() => openViewModal(u)}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-primary btn-sm rounded-circle d-flex justify-content-center align-items-center p-0"
                                    style={{ width: "32px", height: "32px" }}
                                    title="Edit User"
                                    onClick={() => openEditModal(u)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm rounded-circle d-flex justify-content-center align-items-center p-0"
                                    style={{ width: "32px", height: "32px" }}
                                    title="Delete User"
                                    onClick={() => openDeleteModal(u)}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-5 text-muted">
                              <i className="fas fa-user-slash fa-2x mb-2 text-muted opacity-50"></i>
                              <p className="mb-0 font-weight-bold">No records found.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              <div className="card-footer bg-transparent py-3 d-flex justify-content-between align-items-center flex-wrap">
                <div className="text-muted small">
                  Showing Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong> ({totalElements} total items)
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB: MASTER DATA IMPORT */}
          {activeTab === "importData" && (
            <div className="card shadow-sm border-0 mb-4 p-4">
              <div className="d-flex align-items-center mb-3">
                <div className="avatar avatar-md rounded-circle bg-soft-success text-success me-3 flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                  <i className="fas fa-file-excel fa-lg"></i>
                </div>
                <div>
                  <h5 className="font-weight-bold mb-0">Master Data Catalog Import</h5>
                  <small className="text-muted">Upload Excel spreadsheet files (.xlsx, .xls) containing University Master Catalog data.</small>
                </div>
              </div>

              {importStatus && (
                <div className={`alert ${importStatus.includes("successfully") ? "alert-success" : "alert-info"} alert-dismissible fade show my-3`} role="alert">
                  <i className="fas fa-info-circle mr-2"></i>{importStatus}
                </div>
              )}

              <form onSubmit={handleMasterDataImport} className="p-4 rounded-lg mt-3">
                <div className="form-group mb-4">
                  <label className="font-weight-bold mb-2">Select Excel Master Data File <span className="text-danger">*</span></label>
                  <div className="position-relative mt-2">
                    <input
                      type="file"
                      id="masterDataUpload"
                      className="d-none"
                      accept=".xlsx, .xls"
                      onChange={(e) => setExcelFile(e.target.files[0])}
                    />
                    <label 
                      htmlFor="masterDataUpload"
                      className="w-100 p-4 m-0 d-flex flex-column align-items-center justify-content-center text-center"
                      style={{ 
                        border: "2px dashed rgba(212, 175, 55, 0.4)", 
                        borderRadius: "12px", 
                        cursor: "pointer", 
                        background: "rgba(212, 175, 55, 0.03)",
                        transition: "all 0.3s ease",
                        minHeight: "140px"
                      }}
                    >
                      <i className="fas fa-cloud-upload-alt mb-2" style={{ fontSize: "2.5rem", color: "#D4AF37" }}></i>
                      <span className="font-weight-bold mb-1" style={{ color: "#D4AF37", fontSize: "1.05rem" }}>
                        Click to browse file
                      </span>
                      <span className={excelFile ? "text-light mt-2 font-weight-bold" : "text-muted mt-2"}>
                        {excelFile ? excelFile.name : "No file selected"}
                      </span>
                    </label>
                  </div>
                  <small className="form-text text-muted mt-2">Supported formats: Microsoft Excel 97-2003 (.xls) or Workbook (.xlsx)</small>
                </div>
                <button
                  type="submit"
                  className="btn btn-success px-4 font-weight-bold rounded-pill shadow-sm py-2"
                  disabled={importing}
                >
                  {importing ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i> Processing File...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt mr-2"></i> Upload & Import Data
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* TAB: HELP DESK & SUPPORT TICKETS */}
          {activeTab === "helpDesk" && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header py-3 border-bottom d-flex justify-content-end align-items-center flex-wrap">
                <div className="input-group input-group-sm mt-2 mt-sm-0" style={{ width: "260px" }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search queries or user..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                    <p className="mt-2 text-muted font-weight-bold">Loading help requests...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="px-4 py-3">Ticket ID</th>
                          <th className="py-3">Requester</th>
                          <th className="py-3">Email / Contact</th>
                          <th className="py-3">Query / Issue</th>
                          <th className="py-3">Date</th>
                          <th className="py-3">Status</th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHelpDesk && filteredHelpDesk.length > 0 ? (
                          filteredHelpDesk.map((t) => (
                            <tr key={t.id}>
                              <td className="px-4 py-3 font-weight-bold text-primary">#{t.id}</td>
                              <td className="py-3">
                                <strong>{t.userName || `User #${t.userId}`}</strong>
                                {t.userRole && (
                                  <div>
                                    <small className="text-muted">{t.userRole}</small>
                                  </div>
                                )}
                              </td>
                              <td className="py-3">
                                <div>{t.userEmail || "N/A"}</div>
                                {t.userPhone && <small className="text-muted">{t.userPhone}</small>}
                              </td>
                              <td className="py-3" style={{ maxWidth: "280px" }}>
                                <div className="text-truncate" title={t.query}>{t.query}</div>
                              </td>
                              <td className="py-3">{t.createdDate || "N/A"}</td>
                              <td className="py-3">
                                <span className={`badge ${
                                  t.status === "Resolved"
                                    ? "bg-success text-white"
                                    : t.status === "In Progress"
                                    ? "bg-info text-white"
                                    : "bg-warning text-dark"
                                } px-2.5 py-1 rounded-pill font-weight-bold`}>
                                  {t.status || "Pending"}
                                </span>
                              </td>
                              <td className="py-3 text-center">
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                  <button
                                    className="btn btn-outline-info btn-sm rounded-circle d-flex justify-content-center align-items-center p-0"
                                    style={{ width: "32px", height: "32px" }}
                                    title="View Query Details"
                                    onClick={() => {
                                      setSelectedTicket(t);
                                      setShowTicketModal(true);
                                    }}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                  {t.status !== "Resolved" && (
                                    <button
                                      className="btn btn-outline-success btn-sm rounded-circle d-flex justify-content-center align-items-center p-0"
                                      style={{ width: "32px", height: "32px" }}
                                      title="Mark Resolved"
                                      onClick={() => handleUpdateTicketStatus(t.id, "Resolved")}
                                    >
                                      <i className="fas fa-check"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5 text-muted">
                              <i className="fas fa-clipboard-list fa-2x mb-2 text-muted opacity-50"></i>
                              <p className="mb-0 font-weight-bold">No help desk requests found for this organization.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              <div className="card-footer bg-transparent py-3 d-flex justify-content-between align-items-center flex-wrap">
                <div className="text-muted small">
                  Showing Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong> ({totalElements} total items)
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Previous
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
        <Footer />
      </div>

      {/* CREATE USER MODAL */}
      {showCreateModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0">
                  <i className="fas fa-user-plus text-primary mr-2"></i> Add New {roleConfigs[formData.role]?.singular || "User"}
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowCreateModal(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body p-4">
                  {formMsg.text && (
                    <div className={`alert ${formMsg.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show mb-3`} role="alert">
                      {formMsg.text}
                    </div>
                  )}

                  {/* ROW 1: 4 FIELDS SIDE-BY-SIDE */}
                  <div className="d-flex flex-wrap" style={{ gap: "15px" }}>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Phone Number *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="+91 9876543210"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value, whatsappNumber: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {/* ROW 2: 3 FIELDS SIDE-BY-SIDE */}
                  <div className="d-flex flex-wrap mt-3" style={{ gap: "15px" }}>
                    <div style={{ flex: "1 1 30%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Date of Birth *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 30%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Password *</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 30%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Confirm Password *</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-primary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                    {actionLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-check mr-1"></i>} Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0">
                  <i className="fas fa-user-edit text-primary mr-2"></i> Edit Details #{formData.id}
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowEditModal(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body p-4">
                  {formMsg.text && (
                    <div className={`alert ${formMsg.type === "success" ? "alert-success" : "alert-danger"} mb-3`} role="alert">
                      {formMsg.text}
                    </div>
                  )}

                  {/* ALL 4 FIELDS SIDE-BY-SIDE IN A SINGLE ROW */}
                  <div className="d-flex flex-wrap" style={{ gap: "15px" }}>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div style={{ flex: "1 1 22%", minWidth: "180px" }}>
                      <label className="font-weight-bold small mb-1">Phone Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-primary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                    {actionLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-save mr-1"></i>} Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* DELETE USER CONFIRMATION MODAL */}
      {showDeleteModal && selectedUser && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0 text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i> Confirm Delete
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowDeleteModal(false)}>
                  &times;
                </button>
              </div>
              <div className="modal-body p-4 text-center">
                <h6 className="font-weight-bold mb-2">
                  Are you sure you want to delete '{selectedUser.firstName} {selectedUser.lastName}'?
                </h6>
                <p className="text-muted small mb-0">
                  Email: <strong>{selectedUser.email}</strong>
                  <br />
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-primary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm} disabled={actionLoading}>
                  {actionLoading ? <i className="fas fa-spinner fa-spin me-1"></i> : <i className="fas fa-trash-alt me-1"></i>} Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW USER DETAILS MODAL */}
      {showViewModal && selectedUser && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0">
                  <i className="fas fa-user-circle text-primary me-2"></i> Profile Details
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowViewModal(false)}>
                  &times;
                </button>
              </div>
              <div className="modal-body p-4">
                <div className="text-center mb-4">
                  <div className="avatar avatar-lg bg-primary text-dark rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center font-weight-bold shadow-sm" style={{ width: "56px", height: "56px", fontSize: "20px" }}>
                    {(selectedUser.firstName || "U").charAt(0).toUpperCase()}
                  </div>
                  <h5 className="font-weight-bold mb-1">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h5>
                </div>

                <ul className="list-group list-group-flush">
                  <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                    <span className="text-muted small">User ID</span>
                    <strong>#{selectedUser.id}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                    <span className="text-muted small">Email</span>
                    <strong>{selectedUser.email}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                    <span className="text-muted small">Phone Number</span>
                    <strong>{selectedUser.phoneNumber || "N/A"}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                    <span className="text-muted small">Created Date</span>
                    <strong>{selectedUser.createdDate || "N/A"}</strong>
                  </li>
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-primary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW TICKET DETAILS MODAL */}
      {showTicketModal && selectedTicket && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0">
                  <i className="fas fa-headset text-danger me-2"></i> Ticket Details #{selectedTicket.id}
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowTicketModal(false)}>
                  &times;
                </button>
              </div>
              <div className="modal-body p-4">
                <div className="row mb-3">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted font-weight-bold d-block">REQUESTER</small>
                    <h6 className="font-weight-bold mb-0">{selectedTicket.userName || `User #${selectedTicket.userId}`}</h6>
                    <small className="text-muted">{selectedTicket.userEmail || "No Email"}</small>
                  </div>
                  <div className="col-md-3 mb-2">
                    <small className="text-muted font-weight-bold d-block">CREATED DATE</small>
                    <div>{selectedTicket.createdDate || "N/A"}</div>
                  </div>
                  <div className="col-md-3 mb-2">
                    <small className="text-muted font-weight-bold d-block">STATUS</small>
                    <div>
                      <span className={`badge ${
                        selectedTicket.status === "Resolved"
                          ? "bg-success text-white"
                          : selectedTicket.status === "In Progress"
                          ? "bg-info text-white"
                          : "bg-warning text-dark"
                      } px-2.5 py-1 rounded-pill font-weight-bold`}>
                        {selectedTicket.status || "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-top pt-3 mb-3">
                  <small className="text-muted font-weight-bold d-block mb-1">QUERY / ISSUE DESCRIPTION</small>
                  <div className="p-3 bg-light rounded text-dark font-weight-normal" style={{ whiteSpace: "pre-wrap" }}>
                    {selectedTicket.query}
                  </div>
                </div>

                {selectedTicket.image && selectedTicket.image.length > 0 && (
                  <div className="border-top pt-3">
                    <small className="text-muted font-weight-bold mb-2 d-block">ATTACHED SCREENSHOTS / DOCUMENTS</small>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedTicket.image.map((imgUrl, idx) => (
                        <a key={idx} href={imgUrl} target="_blank" rel="noopener noreferrer" className="mr-2 mb-2">
                          <img src={imgUrl} alt={`Attachment ${idx + 1}`} className="img-thumbnail" style={{ height: "90px", objectFit: "cover" }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {selectedTicket.status !== "Resolved" && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, "Resolved")}
                  >
                    <i className="fas fa-check mr-1"></i> Mark as Resolved
                  </button>
                )}
                <button type="button" className="btn btn-outline-primary" onClick={() => setShowTicketModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE HELP DESK TICKET MODAL */}
      {showCreateTicketModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-weight-bold mb-0">
                  <i className="fas fa-headset text-danger mr-2"></i> Submit New Help Request
                </h5>
                <button type="button" className="close border-0 bg-transparent p-0 m-0" style={{ fontSize: "24px", outline: "none" }} onClick={() => setShowCreateTicketModal(false)}>
                  &times;
                </button>
              </div>
              <form onSubmit={handleCreateTicketSubmit}>
                <div className="modal-body p-4">
                  {ticketMsg.text && (
                    <div className={`alert ${ticketMsg.type === "success" ? "alert-success" : "alert-danger"} mb-3`} role="alert">
                      {ticketMsg.text}
                    </div>
                  )}

                  <div className="form-group mb-3">
                    <label className="font-weight-bold small mb-1">Query / Issue Details <span className="text-danger">*</span></label>
                    <textarea
                      className="form-control"
                      rows="5"
                      placeholder="Please describe your issue, request, or technical question..."
                      value={ticketQuery}
                      onChange={(e) => setTicketQuery(e.target.value)}
                      required
                    ></textarea>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-primary" onClick={() => setShowCreateTicketModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger font-weight-bold" disabled={actionLoading}>
                    {actionLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-paper-plane mr-1"></i>} Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuperAdminDashboard;

